import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMyDashboards } from '../api/dashboards.ts';
import { LoadFailure, isSessionFailure } from './LoadFailure.tsx';
import type { DashboardKey } from '../api/types.ts';
import { useKeepState } from './pageSearch.ts';

/**
 * The frame every dashboard sits in.
 *
 * It asks the server which dashboards this person may open — from every role
 * they hold, which `/me` does not carry — before the dashboard itself is
 * mounted. So a person without the role sees why, and the dashboard's own
 * request is never made to be refused.
 *
 * The server still decides. A dashboard route refuses with a 403 whatever this
 * frame thinks, and the pages show that refusal through `LoadFailure`, never as
 * an empty list.
 */
export function DashboardFrame({
  dashboard,
  title,
  subtitle,
  children,
}: {
  dashboard: DashboardKey;
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  const mine = useMyDashboards();
  const open = mine.data?.dashboards ?? [];

  return (
    <Shell title={title} subtitle={subtitle}>
      {mine.error && !mine.data ? (
        <LoadFailure
          what="Your dashboards"
          error={mine.error}
          onRetry={() => void mine.refetch()}
          retrying={mine.isFetching}
        />
      ) : mine.isPending ? (
        <Waiting>Loading…</Waiting>
      ) : open.includes(dashboard) ? (
        children
      ) : (
        <NotOpen />
      )}
    </Shell>
  );
}

function Shell({
  title,
  subtitle,
  nav,
  children,
}: {
  title: string;
  subtitle: string;
  nav?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">{title}</h1>
          <p className="mt-0.5 max-w-[60rem] pb-2 text-sm text-neutral-500">{subtitle}</p>
          {nav}
        </header>
        <div className="page-body">{children}</div>
      </div>
    </div>
  );
}

function NotOpen() {
  return (
    <div role="alert" className="p-6">
      <h2 className="text-lg font-bold text-neutral-900">Not open to your roles</h2>
      <p className="mt-2 text-neutral-700">
        Dashboards follow the roles you hold, and none of yours includes this one.
      </p>
      <p className="mt-1 text-neutral-600">An administrator can add a role if you need it.</p>
      <Link
        to="/clients"
        className="mt-5 inline-flex min-h-[48px] items-center rounded-lg bg-neutral-900 px-5
                   font-semibold text-white active:bg-neutral-700"
      >
        Back to clients
      </Link>
    </div>
  );
}

export function Waiting({ children }: { children: ReactNode }) {
  return <p className="px-4 py-10 text-center text-neutral-500">{children}</p>;
}

/** A filter that is on or off. Pressed state is announced, not only shown. */
export function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border px-3 text-[0.9375rem]
                  font-semibold tabular-nums ${
                    active
                      ? 'border-neutral-900 bg-neutral-900 text-white'
                      : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-500'
                  }`}
    >
      {children}
    </button>
  );
}

export function Pager({
  offset,
  total,
  pageSize,
  onOffset,
}: {
  offset: number;
  total: number;
  pageSize: number;
  onOffset: (offset: number) => void;
}) {
  if (total <= pageSize) return null;
  const to = Math.min(offset + pageSize, total);
  const button =
    'min-h-[44px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-700 ' +
    'active:bg-neutral-100 disabled:bg-neutral-100 disabled:text-neutral-600 disabled:cursor-not-allowed';
  return (
    <div className="flex items-center gap-3 border-t border-neutral-200 px-4 py-3 lg:px-5">
      <p className="text-sm text-neutral-500 tabular-nums">
        {(offset + 1).toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </p>
      <button
        type="button"
        className={`ml-auto ${button}`}
        disabled={offset === 0}
        onClick={() => onOffset(Math.max(0, offset - pageSize))}
      >
        Previous
      </button>
      <button type="button" className={button} disabled={to >= total} onClick={() => onOffset(to)}>
        Next
      </button>
    </div>
  );
}

/**
 * Filters live in the URL, so a filtered dashboard survives a reload and can be
 * handed on. Every one is a fixed value (url-guard.ts's PAGE_QUERY_KEYS); free
 * text never goes here (pageSearch.ts), and a filter change keeps it.
 */
export function useUrlFilters() {
  const [params, setParams] = useSearchParams();
  const keep = useKeepState();
  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, keep);
  };
  return [params, update] as const;
}

/**
 * What the rows on screen may claim — the client list's rule, shared.
 *
 * Rows render only when they are an answer. Placeholder rows under a failed
 * request are the previous filter's, not this one's, so the failure takes the
 * screen. A 403 here is the dashboard refusing, not the session.
 */
export function listState(query: {
  error: Error | null;
  failureReason: Error | null;
  isPlaceholderData: boolean;
  data: unknown;
}) {
  const failure = query.error ?? (query.isPlaceholderData ? query.failureReason : null);
  const takeover =
    failure !== null &&
    (isSessionFailure(failure, { forbiddenIsSession: false }) || !query.data || query.isPlaceholderData);
  return { failure, takeover };
}

/** Exact decimal strings from the server, shown as dollars. Display only. */
export function formatMoney(amount: string): string {
  return Number(amount).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}
